import { SPEED, STEP_SECONDS, step } from './movement';
import type { Entity, Vec3, WorldMap } from './protocol';

export interface Direction { x: number; z: number; yaw?: number }
interface PendingInput { seq: number; direction: Direction; sent: number }
export const MAX_PENDING_INPUTS = 32;
export const MAX_PREDICTION_AGE_MS = 1000;
export const HARD_CORRECTION_METERS = 1;
const zero = (): Vec3 => ({ x: 0, y: 0, z: 0 });
export const copyEntity = (entity: Entity): Entity => ({ ...entity, position: { ...entity.position }, rotation: [...entity.rotation] });
export const distance = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

export function advanceEntity(entity: Entity, direction: Direction, dt: number, map: WorldMap): Entity {
  const result = copyEntity(entity);
  result.position = step(entity.position, direction, dt, map);
  result.moving = distance(entity.position, result.position) > 1e-9;
  if (direction.yaw !== undefined || result.moving) { const yaw = direction.yaw ?? Math.atan2(direction.x, direction.z); result.rotation = [0, Math.sin(yaw / 2), 0, Math.cos(yaw / 2)]; }
  return result;
}

/** Simulation prediction and render-only correction are deliberately separate. */
export class Predictor {
  private current: Entity | null = null;
  private pending: PendingInput[] = [];
  private offset = zero();
  private highestSequence = 0;
  private barrier = 0;
  private lastAck = 0;
  corrections = 0;
  hardSnaps = 0;
  maxCorrection = 0;
  lastCorrection = 0;
  constructor(private map: WorldMap) {}
  updateMap(map: WorldMap): void { this.map = map; }

  get pendingCount(): number { return this.pending.length; }
  get waiting(): boolean { return this.barrier !== 0; }
  get entity(): Entity | null { return this.current && copyEntity(this.current); }
  tooOld(now: number): boolean { return this.pending.length > 0 && now - this.pending[0]!.sent > MAX_PREDICTION_AGE_MS; }

  input(seq: number, direction: Direction, now: number): boolean {
    if (!this.current || this.waiting || this.pending.length >= MAX_PENDING_INPUTS || seq <= this.highestSequence) return false;
    this.highestSequence = seq; this.pending.push({ seq, direction: { ...direction }, sent: now });
    this.current = advanceEntity(this.current, direction, STEP_SECONDS, this.map); return true;
  }
  stop(seq: number): void {
    this.highestSequence = Math.max(seq, this.highestSequence); this.barrier = seq; this.pending = [];
    if (this.current) this.current.moving = false;
  }
  reconcile(authoritative: Entity): boolean {
    if (authoritative.ack < this.lastAck || (this.barrier && authoritative.ack < this.barrier)) return false;
    if (this.current && authoritative.ack > this.highestSequence) throw new Error('Server acknowledged unsent input');
    this.lastAck = authoritative.ack; this.barrier = 0;
    this.pending = this.pending.filter(input => input.seq > authoritative.ack);
    let replayed = copyEntity(authoritative);
    for (const input of this.pending) replayed = advanceEntity(replayed, input.direction, STEP_SECONDS, this.map);
    if (this.current) {
      const error = distance(this.current.position, replayed.position);
      this.lastCorrection = error; this.maxCorrection = Math.max(this.maxCorrection, error);
      const discontinuity = authoritative.motion !== this.current.motion;
      if (error > 1e-5 || discontinuity) {
        this.corrections++;
        if (error >= HARD_CORRECTION_METERS || discontinuity) { this.offset = zero(); this.hardSnaps++; }
        else {
          this.offset.x += this.current.position.x - replayed.position.x;
          this.offset.z += this.current.position.z - replayed.position.z;
          if (Math.hypot(this.offset.x, this.offset.z) >= HARD_CORRECTION_METERS) { this.offset = zero(); this.hardSnaps++; }
        }
      }
    }
    this.current = replayed; return true;
  }
  sample(direction: Direction, partialMS: number, frameSeconds: number): Entity | null {
    if (!this.current) return null;
    // Preview the fraction of the next fixed step, without sending fractional time.
    const result = this.waiting ? copyEntity(this.current) : advanceEntity(this.current, direction, Math.max(0, Math.min(partialMS, 50)) / 1000, this.map);
    if (!this.waiting && partialMS === 0) result.moving = (direction.x !== 0 || direction.z !== 0) && this.current.moving;
    const decay = Math.exp(-Math.max(0, frameSeconds) / .1); this.offset.x *= decay; this.offset.z *= decay;
    const length = Math.hypot(this.offset.x, this.offset.z);
    if (length > 1e-6) {
      // Sweep the render correction too: a visual offset must not put us in a wall.
      result.position = step(result.position, { x: this.offset.x / length, z: this.offset.z / length }, length / SPEED, this.map);
    }
    return result;
  }
}
